/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useEffect, useState } from 'react';
import { useHistory, useParams, matchPath } from 'react-router-dom';
import { useLocationWithState } from 'hooks/useLocationWithState';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ProfileAvatar, SimpleHeader, Spinner, Modal } from 'ui/components';
import { DomainUtils, Ducks, Role, XError } from '@wit/vtv-x-foundation-lib';
import { PrivateRoutePath, StartUpRoutePath } from 'routes/paths';
import translate from 'services/i18n/translate';
import * as StartUp from 'store/ducks/startUp';
import * as ActionsWatcher from 'store/ducks/analytics/actionsWatcher';
import { addSettingsErrorEvent } from 'store/ducks/analytics/systemEvent';
import { EProfilePageStatus } from './profile.interface';
import { ButtonType } from '../../components/Button';

const WAITING_TIME_AFTER_UPDATE_MS = 1500;

interface IProfileProps {
  mode: EProfilePageStatus;
}

interface UriParams {
  id: string;
}

interface FeedbackMessageProps {
  isNameInUse?: boolean;
  isLoading?: boolean;
  isProcessedUpdateAction?: boolean;
  updateActionError?: XError;
  error?: XError;
}

// Generates the title and link regarding the parent route
const prepareHeaderProps = (mode: EProfilePageStatus, fromPath: string) => {
  const title =
    mode === EProfilePageStatus.CREATE
      ? translate['profiles.addProfileTitle']
      : translate['profiles.updatedProfileTitle'];

  const fromProfiles = matchPath(fromPath, { path: PrivateRoutePath.PROFILES_LIST });
  if (fromProfiles) {
    return {
      subtitle: title,
      backLabel: translate['profiles.selectYourProfileTitle'],
      backUrl: fromPath,
    };
  }

  return {
    subtitle: title,
    backLabel: translate['menu.profile.settings'],
    backUrl: PrivateRoutePath.SETTINGS,
  };
};

const getFeedbackMessage = ({
  isNameInUse,
  isLoading,
  isProcessedUpdateAction,
  updateActionError,
  error,
}: FeedbackMessageProps) => {
  if (isProcessedUpdateAction && !updateActionError) {
    return (
      <span id="Profile_feedbackMessage" className="generic-message">
        <span className="icon icon-success" />
        <span className="message">{translate['profiles.updateSuccess']}</span>
      </span>
    );
  }

  if (!isLoading && (error || isNameInUse)) {
    return (
      <span id="Profile_feedbackMessage" className="generic-message">
        <span className="icon icon-error" />
        <span className="message">
          {isNameInUse ? translate['error.authentication.nameAlreadyInUse'] : error?.translation}
        </span>
      </span>
    );
  }

  return null;
};

const nameMaxLength = 40;

const Profile: React.FunctionComponent<IProfileProps> = (props: IProfileProps) => {
  const { mode } = props;
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocationWithState();

  const { id: userId } = useParams<UriParams>();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isNameInUse, setIsNameInUse] = useState(false);
  const [name, setName] = useState('');

  const users = useSelector(Ducks.Household.Selectors.data.getUsers);
  const maxProfiles = useSelector(Ducks.Household.Selectors.data.usersLimit);
  const isConverged = useSelector(Ducks.Household.Selectors.data.getIsConverged);
  const isProcessingAddAction = useSelector(Ducks.Household.Selectors.actions.createUser.isProcessing);
  const isProcessingRemoveAction = useSelector(Ducks.Household.Selectors.actions.removeUser.isProcessing);
  const isProcessingUpdateAction = useSelector(Ducks.Household.Selectors.actions.updateUser.isProcessing);
  const isProcessedAddAction = useSelector(Ducks.Household.Selectors.actions.createUser.isProcessed);
  const isProcessedRemoveAction = useSelector(Ducks.Household.Selectors.actions.removeUser.isProcessed);
  const isProcessedUpdateAction = useSelector(Ducks.Household.Selectors.actions.updateUser.isProcessed);

  const addActionError = useSelector(Ducks.Household.Selectors.actions.createUser.getError);
  const removeActionError = useSelector(Ducks.Household.Selectors.actions.removeUser.getError);
  const updateActionError = useSelector(Ducks.Household.Selectors.actions.updateUser.getError);

  const isLoading = isProcessingAddAction || isProcessingRemoveAction || isProcessingUpdateAction;
  const error = addActionError || removeActionError || updateActionError;
  const houseHoldName = translate['profiles.household.name'];

  useEffect(() => {
    if (error) {
      addSettingsErrorEvent(error);
    }
  }, [error]);

  const headerProps = prepareHeaderProps(mode, location.from || '');

  useEffect(() => {
    if (mode !== EProfilePageStatus.EDIT) {
      const selectableUsers = DomainUtils.getSelectableUsers(isConverged, users);
      if (mode === EProfilePageStatus.CREATE && selectableUsers?.length >= maxProfiles) {
        history.replace(headerProps.backUrl);
      }
      return;
    }
    const profileToEdit = users.find((u) => u.id === userId);
    if (!profileToEdit) {
      history.replace(PrivateRoutePath.SETTINGS);
      return;
    }
    setName(profileToEdit?.firstName || '');
  }, []);

  useEffect(() => {
    return () => {
      if (mode === EProfilePageStatus.CREATE) {
        dispatch(Ducks.Household.Creators.createUser.reset());
        return;
      }

      dispatch(Ducks.Household.Creators.updateUser.reset());
      dispatch(Ducks.Household.Creators.removeUser.reset());
    };
  }, []);

  useEffect(() => {
    setIsNameInUse(
      users.some(
        (u) =>
          (u.firstName?.toLowerCase() === name.trim().toLowerCase() &&
            userId !== u.id &&
            u.role !== Role.MASTER &&
            u.role !== Role.DEFAULT) ||
          name.trim().toLowerCase() === houseHoldName.toLowerCase()
      )
    );
  }, [name]);

  useEffect(() => {
    if (users) {
      dispatch(ActionsWatcher.ActionCreators.setUsers(users));
    }
  }, [dispatch, users]);

  useEffect(() => {
    if (isProcessedAddAction && !addActionError && mode === EProfilePageStatus.CREATE) {
      dispatch(StartUp.ActionCreators.init(undefined, true, true));
      history.replace(StartUpRoutePath.ROOT);
    }
  }, [isProcessedAddAction, addActionError, mode, history, dispatch]);

  useEffect(() => {
    if (isProcessedRemoveAction && !removeActionError && mode === EProfilePageStatus.EDIT) {
      dispatch(StartUp.ActionCreators.init(undefined, true));
      history.replace(StartUpRoutePath.ROOT);
    }
  }, [isProcessedRemoveAction, removeActionError, mode, history, dispatch]);

  useEffect(() => {
    let timeoutRoutineId: ReturnType<typeof setInterval> | undefined;
    if (isProcessedUpdateAction && !updateActionError && mode === EProfilePageStatus.EDIT) {
      timeoutRoutineId = setTimeout(() => {
        history.push(PrivateRoutePath.SETTINGS);
      }, WAITING_TIME_AFTER_UPDATE_MS);
    }
    return () => {
      if (timeoutRoutineId) {
        clearTimeout(timeoutRoutineId);
      }
    };
  }, [isProcessedUpdateAction, updateActionError, mode, history]);

  const handleCreation = () => {
    if (name.trim().length > 0) {
      dispatch(Ducks.Household.Creators.createUser.request(name.trim()));
    }
  };

  const handleUpdate = () => {
    if (userId && name.trim().length > 0) {
      dispatch(Ducks.Household.Creators.updateUser.request(userId, name.trim()));
    }
  };

  const handleDeleteProfile = () => {
    setShowConfirmationModal(false);
    if (userId) {
      dispatch(Ducks.Household.Creators.removeUser.request(userId));
    }
  };

  const containerCssClass = mode === EProfilePageStatus.CREATE ? 'new-profile' : 'edit-profile';

  return (
    <>
      <div
        id="ManageProfile_container"
        className={`template-holder back-nav authentication sign-in manage-profile ${containerCssClass}`}
      >
        <SimpleHeader subtitle={headerProps.subtitle} backLabel={headerProps.backLabel} backUrl={headerProps.backUrl} />
        <div className="component no-background">
          <div className="container-avatar">
            <div className="avatar default">
              {name.length ? <ProfileAvatar profileId={userId} name={name} /> : <span className="defaultAvatar" />}
            </div>
          </div>
          <Spinner isActive={isLoading} isEmbedded />

          {getFeedbackMessage({ isNameInUse, isLoading, isProcessedUpdateAction, updateActionError, error })}

          <div className="group">
            <input
              id="ManageProfile_input"
              type="text"
              name="name"
              value={name}
              disabled={isLoading && mode !== EProfilePageStatus.LIST}
              onChange={(e) => setName(e.target.value)}
              maxLength={nameMaxLength}
              placeholder={translate['profiles.name']}
            />
          </div>
          {mode === EProfilePageStatus.CREATE && (
            <div className="group">
              <Button
                bemBlock="ManageProfile"
                bemModifier="save"
                buttonType={ButtonType.BUTTON}
                disabled={isLoading || isNameInUse}
                text={translate['profiles.buttonSaveLabel']}
                onClick={handleCreation}
              />
            </div>
          )}
          {mode === EProfilePageStatus.EDIT && (
            <div className="group">
              <div className="btn-group-container">
                <Button
                  bemBlock="ManageProfile"
                  bemModifier="cancel"
                  buttonType={ButtonType.BUTTON}
                  disabled={isLoading}
                  text={translate['profiles.buttonCancelLabel']}
                  onClick={() => history.push(headerProps.backUrl)}
                />
                <Button
                  bemBlock="ManageProfile"
                  bemModifier="update"
                  buttonType={ButtonType.BUTTON}
                  disabled={isLoading || isNameInUse}
                  text={translate['profiles.buttonUpdateLabel']}
                  onClick={handleUpdate}
                />
              </div>

              <div className="links-container">
                <a
                  id="ManageProfile_removeProfile"
                  onClick={() => setShowConfirmationModal(true)}
                  className="link-modal"
                >
                  <span>{translate['profiles.removeProfile']}</span>
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
      <Modal
        customClassName="profile-confirmation-modal"
        avatar={<ProfileAvatar profileId={`modal-${userId}`} className="type" name={name} />}
        title={name}
        message={translate['profiles.removeProfileTitle']}
        buttons={[
          {
            id: 'cancel',
            text: translate['modal.button.cancel'],
            onClick: () => setShowConfirmationModal(false),
          },
          {
            id: 'confirm',
            text: translate['modal.button.confirm'],
            onClick: handleDeleteProfile,
          },
        ]}
        isShowing={showConfirmationModal}
        setIsShowing={() => setShowConfirmationModal(false)}
      />
    </>
  );
};

export default Profile;
